import { useEffect, useRef, useState } from 'react';
import { useStore } from '@nanostores/react';
import { History } from 'lucide-react';
import { listCheckpoints } from '~/lib/api/chats';
import { buildVersionList, type VersionItem } from './version-list';
import { $peekVersion } from '~/lib/stores/version-peek';
import { classNames } from '~/utils/classNames';
import { createScopedLogger } from '~/utils/logger';

const logger = createScopedLogger('VersionSelector');

/** Clock dropdown in the preview toolbar: pick a version to peek it read-only. */
export function VersionSelector({ chatId }: { chatId: string }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<VersionItem[]>([]);
  const peek = useStore($peekVersion);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    listCheckpoints(chatId)
      .then((cps) => setItems(buildVersionList(cps)))
      .catch((err) => {
        logger.warn('Failed to load checkpoint versions', err);
        setItems([]);
      });

    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);

    return () => document.removeEventListener('mousedown', onClick);
  }, [open, chatId]);

  if (!chatId) {
    return null;
  }

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={classNames('ui-btn-icon', (open || peek) && 'text-accent hover:text-accent')}
        title="Version history"
      >
        <History className="w-4 h-4" />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1.5 z-50 min-w-[200px] max-h-[280px] overflow-y-auto rounded-lg border border-hairline bg-raised shadow-lg py-1">
          <button
            type="button"
            onClick={() => {
              $peekVersion.set(null);
              setOpen(false);
            }}
            className={classNames(
              'flex items-center justify-between w-full px-3 py-2 text-xs hover:bg-sunken transition-colors',
              peek === null ? 'text-accent' : 'text-secondary',
            )}
          >
            Current
          </button>
          {items.map((item) => (
            <button
              key={item.messageId}
              type="button"
              disabled={!item.hasPreview}
              onClick={() => {
                if (!item.hasPreview) {
                  return;
                }

                $peekVersion.set(item.messageId);
                setOpen(false);
              }}
              className={classNames(
                'flex items-center justify-between w-full px-3 py-2 text-xs transition-colors',
                item.hasPreview ? 'hover:bg-sunken' : 'opacity-50 cursor-not-allowed',
                peek === item.messageId ? 'text-accent' : 'text-secondary',
              )}
            >
              <span>
                {item.label}
                {item.kind === 'restore' ? ' · restore point' : ''}
                {!item.hasPreview ? ' · no preview' : ''}
              </span>
              <span className="text-muted">
                {new Date(item.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </span>
            </button>
          ))}
          {items.length === 0 && <div className="px-3 py-2 text-xs text-muted">No versions yet</div>}
        </div>
      )}
    </div>
  );
}
